"use client";

import React, { useEffect, useRef } from "react";
import {
  Avatar,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  useOverlayState,
  TextArea,
  Link,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownPopover,
  DropdownSection,
  DropdownTrigger,
  cn,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth, useUser } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { ModalOverlay, ModalHeader, ModalBody } from "@/components/UI/ModalOverlay";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { availableLanguages } from "@/i18n";
import { useTheme } from "@/hooks/use-theme";
import { useGlobalStore } from "@/stores/GlobalStore";
import { useChatStore } from "@/stores/ChatStore";
import { useChatHistoryStore } from "@/stores/ChatHistoryStore";
import { useChatURLState } from "@/hooks/useChatURLState";
import { logger } from "@/telemetry";

import { I18nIcon, LanguageSwitch } from "../language-switch";
import { ChatHistoryComponent } from "../Chat/ChatHistory";
import { SearchChatButton } from "../Chat/SearchChatButton";
import { ChatHistorySearchModal } from "../Chat/ChatHistorySearchModal";
import { ModelInfoIcon } from "../icons";

import { useSidebarMenuItems } from "./SidebarMenuItems";
import Sidebar from "./Sidebar";
import { useSidebar } from "./SidebarContext";

// import { SearchIcon } from "@/components/icons";

export function SidebarLayoutInvestAI({ children }: { children: React.ReactNode }) {
  const { userProfile } = useUser();
  const { newChat, load, triggerResetPrompt } = useChatStore();
  const {
    fetchChatHistory,
    displayedChats,
    chatHistory,
    selectChat,
    selectAndEnsureVisible,
    clearSelection,
  } = useChatHistoryStore();
  const { user, isAuthenticated } = useAuth();
  const { adminClient } = useWebuildAPIClient();
  const { sidebar } = useGlobalStore();
  const { isSidebarCompact, toggleSidebarCompact } = sidebar;
  const { t } = useTranslation();
  const { logout } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const currentPath = pathname.split("/")?.[1];
  const navigate = useNavigate();
  const { showChatHistory, setShowChatHistory } = useSidebar();
  const { navigateToChat, clearChatId, chatId: chatIdFromURL } = useChatURLState();
  const { workflowId: urlWorkflowId } = useParams<{ workflowId?: string }>();

  // Determine the current workflow ID from URL or default to chat_agent
  const currentWorkflowId = urlWorkflowId || "chat_agent";

  useEffect(() => {
    const shouldShowHistory = location.pathname.startsWith("/chat");

    if (shouldShowHistory !== showChatHistory) {
      setShowChatHistory(shouldShowHistory);
    }
  }, [location.pathname, showChatHistory, setShowChatHistory]);

  // Track the last chat ID we handled from URL to avoid re-processing on displayedChats changes
  const lastHandledUrlChatIdRef = useRef<string | null>(null);

  // Select chat from URL when chat history is loaded (initial page load only)
  useEffect(() => {
    // Skip if no chat ID in URL or already handled this chat ID
    if (!chatIdFromURL || !adminClient || displayedChats.length === 0) {
      return;
    }

    // Skip if we already handled this chat ID (prevents re-running on displayedChats changes)
    if (lastHandledUrlChatIdRef.current === chatIdFromURL) {
      return;
    }

    logger.info("🔗 SidebarLayout: URL has chat ID, selecting and loading", { chatIdFromURL });
    lastHandledUrlChatIdRef.current = chatIdFromURL;

    // Use selectAndEnsureVisible to fetch the chat if it's not in the displayed list
    selectAndEnsureVisible(chatIdFromURL, adminClient);

    // Load the chat data if it's not already loaded
    load(adminClient, chatIdFromURL);
  }, [chatIdFromURL, displayedChats.length, selectAndEnsureVisible, adminClient, load]);

  const handleSelectChat = async (chatHistoryId: string) => {
    if (!adminClient) return;

    // Mark this chat ID as handled BEFORE navigation to prevent URL effect from re-processing
    lastHandledUrlChatIdRef.current = chatHistoryId;

    try {
      // Validate chat exists in history
      const isValid = selectChat(chatHistoryId);

      if (!isValid) {
        logger.error("Chat not found in history");

        return;
      }

      // Navigate to chat URL (IMMEDIATE - user requirement)
      navigateToChat(chatHistoryId, currentWorkflowId);

      // Load chat data from backend
      await load(adminClient, chatHistoryId);
      triggerResetPrompt();
    } catch (error: any) {
      logger.error("Failed to load chat", error);

      // Handle specific HTTP error codes
      if (error?.status === 403) {
        logger.error("You don't have access to this chat");
      } else if (error?.status === 404) {
        logger.error("Chat not found or has been deleted");
      } else {
        logger.error("Failed to load chat. Please try again.");
      }

      // Navigate back to base route on error
      const basePath =
        currentWorkflowId === "chat_agent" ? "/chat" : `/workflows/${currentWorkflowId}`;

      navigate(basePath, { replace: true });
    }
  };

  /**
   * Handler for search modal selections.
   * Unlike handleSelectChat, this uses selectAndEnsureVisible to handle
   * chats that may not be in the local cache (search results from API).
   */
  const handleSearchSelectChat = async (chatHistoryId: string) => {
    if (!adminClient) return;

    // Mark this chat ID as handled BEFORE navigation to prevent URL effect from re-processing
    lastHandledUrlChatIdRef.current = chatHistoryId;

    try {
      // Use selectAndEnsureVisible to fetch the chat if it's not in the displayed list
      // This is necessary for search results which may include chats not in local cache
      await selectAndEnsureVisible(chatHistoryId, adminClient);

      // Navigate to chat URL (IMMEDIATE - user requirement)
      navigateToChat(chatHistoryId, currentWorkflowId);

      // Load chat data from backend
      await load(adminClient, chatHistoryId);
      triggerResetPrompt();
    } catch (error: any) {
      logger.error("Failed to load chat from search", error);

      // Handle specific HTTP error codes
      if (error?.status === 403) {
        logger.error("You don't have access to this chat");
      } else if (error?.status === 404) {
        logger.error("Chat not found or has been deleted");
      } else {
        logger.error("Failed to load chat. Please try again.");
      }

      // Navigate back to base route on error
      const basePath =
        currentWorkflowId === "chat_agent" ? "/chat" : `/workflows/${currentWorkflowId}`;

      navigate(basePath, { replace: true });
    }
  };

  const handleNewChat = () => {
    // Clear the chat ID from URL first
    clearChatId();

    // Create a new chat session
    newChat();

    // Clear the chat history sidebar selection
    clearSelection();

    triggerResetPrompt();

    // Focus the chat input field after a short delay
    setTimeout(() => {
      const textarea = document.querySelector(
        'textarea[placeholder*="Start new chat"]',
      ) as HTMLTextAreaElement;

      if (textarea) {
        textarea.focus();
        logger.info("🆕 handleNewChat: Chat input focused");
      }
    }, 150);

    // Fetch chat history again to refresh the list (after clearing selection)
    // Use setTimeout to ensure clearSelection() completes first
    if (adminClient && user?.id) {
      setTimeout(() => {
        logger.info("🆕 handleNewChat: Fetching chat history after clear");
        fetchChatHistory(user.id, adminClient);
      }, 10); // Increased from 0 to 10ms
    }
  };

  const { menuItems: sidebarMenuItems } = useSidebarMenuItems();
  const {
    isOpen: isModalOpen,
    open: onModalOpen,
    close: onModalClose,
    setOpen: onModalSetOpen,
  } = useOverlayState();
  const {
    isOpen: isHelpModalOpen,
    open: onHelpModalOpen,
    setOpen: onHelpModalSetOpen,
  } = useOverlayState();
  const {
    isOpen: isSearchModalOpen,
    open: onSearchModalOpen,
    setOpen: onSearchModalSetOpen,
  } = useOverlayState();

  // Keyboard shortcut: Cmd+K (Mac) / Ctrl+K (Windows/Linux) to open search
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        // Only trigger if on chat page with history visible
        if (showChatHistory && displayedChats.length > 0) {
          e.preventDefault(); // Prevent browser default (Chrome address bar focus)
          e.stopPropagation();
          onSearchModalOpen();
        }
      }
    };

    // Use capture phase to intercept events before they reach child elements
    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [showChatHistory, displayedChats.length, onSearchModalOpen]);

  const { brand } = useBrand();
  const { theme, isDark } = useTheme();

  const BrandIcon = theme === "dark" && brand.iconDark ? brand.iconDark : brand.icon;
  const SmallBrandIcon =
    theme === "dark" && brand.smallIconDark ? brand.smallIconDark : brand.smallIcon || BrandIcon;
  const BrandLogo = theme === "dark" && brand.logoDark ? brand.logoDark : brand.logo;

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const feedback = formData.get("feedback") as string;

    try {
      if (!adminClient) {
        throw new Error("Admin client not found");
      }
      await adminClient.feedback.createFeedback({
        feedback,
      });
      onModalClose();
    } catch (error) {
      logger.error("Failed to submit feedback", error);
    }
  };

  return (
    <div className="flex h-dvh w-full">
      {isAuthenticated && brand.name && userProfile?.name && (
        <div
          className={cn(
            "relative flex h-full flex-col !border-r-small border-divider transition-[width] duration-150 ease-out motion-reduce:transition-none light:bg-brand-secondary",
            isSidebarCompact ? "w-16 items-center px-2 overflow-hidden" : "w-65",
            theme === "light" && "bg-brand-secondary",
          )}
          style={{
            borderColor: "var(--brand-tertiary) !important",
          }}
        >
          {/* Sidebar header: logo and toggle button with smooth transition */}
          {isSidebarCompact ? (
            // Collapsed: logo on top, toggle button below
            <div className="flex flex-col items-center pt-4 gap-2">
              {/* Logo */}
              <Link
                aria-label="Home"
                className="flex items-center justify-center"
                color="foreground"
                href="/"
              >
                <SmallBrandIcon size={24} />
              </Link>
              {/* Toggle Button */}
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    isIconOnly
                    aria-label="Expand sidebar"
                    className="!m-0"
                    size="sm"
                    variant="ghost"
                    onPress={toggleSidebarCompact}
                  >
                    <Icon
                      className="text-default-400"
                      height={20}
                      icon="solar:sidebar-minimalistic-linear"
                      width={20}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent showArrow placement="right">
                  {t("Open sidebar")}
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            // Expanded: logo left, toggle right
            <div className="flex items-center justify-between p-4 w-full">
              <Link aria-label="Home" className="flex items-center p-2" color="foreground" href="/">
                <BrandLogo className="mx-auto" size={40} />
              </Link>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    isIconOnly
                    aria-label={t("Close sidebar")}
                    size="sm"
                    variant="ghost"
                    onPress={toggleSidebarCompact}
                  >
                    <Icon
                      className="text-default-400"
                      height={24}
                      icon="solar:sidebar-minimalistic-line-duotone"
                      width={24}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent showArrow placement="bottom">
                  {t("Close sidebar")}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          {/* End sidebar header */}
          <Button
            className={cn("btn-brand-primary text-sm mx-4 h-10 mb-2", {
              "mt-6": isSidebarCompact,
            })}
            data-id="new-chat-button"
            isIconOnly={isSidebarCompact}
            size="md"
            onPress={handleNewChat}
          >
            <img
              alt=""
              height={18}
              src={`/brands/${brand.prefix || "ii"}/pathfinder.svg`}
              style={{ borderRadius: 0, filter: "invert(1) brightness(2)" }}
            />
            {!isSidebarCompact && t("New Chat")}
          </Button>
          {/* Main sidebar content: Sidebar + ChatHistory, scrollable */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* <div> */}
            <Sidebar
              className="px-4 py-2"
              defaultSelectedKey="home"
              isCompact={isSidebarCompact}
              items={sidebarMenuItems}
              selectedKeys={[currentPath]}
              showMenuTitle={false}
            />
            {isSidebarCompact && showChatHistory && displayedChats.length > 0 && (
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    isIconOnly
                    aria-label={t("Search chat history")}
                    className="mx-auto mb-2"
                    data-id="compact-search-button"
                    size="sm"
                    variant="ghost"
                    onPress={onSearchModalOpen}
                  >
                    <Icon className="text-default-500" icon="solar:magnifer-linear" width={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent placement="right">{t("Search chat history")}</TooltipContent>
              </Tooltip>
            )}
            {!isSidebarCompact && showChatHistory && (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex items-center justify-between px-[30px] py-2 flex-shrink-0">
                  <span className="font-bold text-brand-primary text-sm dark:text-foreground!">
                    {t("Chat History")}
                  </span>
                  <SearchChatButton
                    isCompact={true}
                    isDisabled={displayedChats.length === 0}
                    onOpenSearch={onSearchModalOpen}
                  />
                </div>
                <div className="flex-1 min-h-0">
                  <ChatHistoryComponent
                    isShowHistory={true}
                    sectionClasses={{
                      base: "px-4",
                    }}
                    showScroll={true}
                    onNewChat={handleNewChat}
                    onSelectChat={handleSelectChat}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer always at the bottom */}
          <div
            className={cn("mt-auto flex flex-col flex-shrink-0", {
              "items-center": isSidebarCompact,
            })}
          >
            {userProfile?.name && (
              <div className="flex flex-col gap-4 mt-3 mb-2 px-4" data-id="user-profile-container">
                <Dropdown data-id="user-dropdown" placement="top-end">
                  <DropdownTrigger>
                    {/* Tooltip is enabled only when the sidebar is collapsed: the name is
                        hidden in compact mode, so hovering the avatar surfaces who is signed
                        in. Wrapping the div (not the trigger Button) keeps the dropdown's
                        press behaviour intact. */}
                    <Tooltip isDisabled={!isSidebarCompact}>
                      <TooltipTrigger>
                        <div
                          className={`btn-brand-primary flex items-center gap-2 text-sm h-10 px-3 rounded-lg cursor-pointer ${isSidebarCompact ? "justify-center" : "justify-start"}`}
                          data-id="user-dropdown-button"
                          role="button"
                          tabIndex={0}
                        >
                          <Avatar
                            className="flex-shrink-0 w-5 h-5 text-tiny"
                            data-id="user-avatar"
                            src={userProfile?.picture || undefined}
                          />
                          {!isSidebarCompact && (
                            <span className="text-brand-secondary break-words" data-id="user-name">
                              {userProfile?.name}
                            </span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent placement="right">
                        <div className="flex flex-col" data-id="user-info-tooltip">
                          <span className="text-xs font-medium">{userProfile?.name}</span>
                          {userProfile?.email && (
                            <span className="text-tiny opacity-70">{userProfile.email}</span>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </DropdownTrigger>
                  <DropdownPopover placement="top end">
                    <DropdownMenu
                      aria-label="Profile Actions"
                      className="w-[210px] bg-content1 px-[8px] py-[8px]"
                      data-id="profile-dropdown-menu"
                      variant="secondary"
                    >
                      <DropdownSection
                        aria-label="profile-section-3"
                        className="mb-0"
                        data-id="dropdown-section-3"
                      >
                        <DropdownItem
                          key="model-info"
                          className="py-[4px] text-default-500"
                          data-id="dropdown-item-model-info"
                          textValue="Models Overview"
                          onPress={() => navigate("/models")}
                        >
                          <div className="flex items-center gap-2">
                            <ModelInfoIcon size={20} />
                            <span className="text-small font-medium text-default-500">
                              {t("Models Overview")}
                            </span>
                          </div>
                        </DropdownItem>

                        <DropdownItem
                          key="settings"
                          className="py-[4px] text-default-500"
                          data-id="dropdown-item-settings"
                          textValue="Settings"
                          onPress={() => navigate("/settings")}
                        >
                          <div className="flex items-center gap-2">
                            <Icon icon="solar:settings-outline" width={20} />
                            <span className="text-small font-medium text-default-500">
                              {t("Settings")}
                            </span>
                          </div>
                        </DropdownItem>
                        <DropdownItem
                          key="logout"
                          className="pt-[4px] text-default-500"
                          data-id="dropdown-item-logout"
                          textValue="Log Out"
                          onPress={() => logout()}
                        >
                          <div className="flex items-center gap-2">
                            <Icon icon="solar:logout-3-outline" width={20} />
                            <span className="text-small font-medium text-default-500">
                              {t("Log Out")}
                            </span>
                          </div>
                        </DropdownItem>
                      </DropdownSection>
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
            )}
            <div className="px-4">
              <LanguageSwitch
                availableLanguages={availableLanguages}
                data-id="language-switch"
                icon={I18nIcon}
                isSidebarCompact={isSidebarCompact}
              />
            </div>
            {brand.showNeedHelp && (
              <Tooltip isDisabled={!isSidebarCompact}>
                <TooltipTrigger>
                  <Button
                    className={`btn-brand-primary text-sm mx-4 my-2 h-10 ${isSidebarCompact ? "justify-center" : "justify-start"}`}
                    data-id="need-help-button"
                    isIconOnly={isSidebarCompact}
                    size="md"
                    onPress={onHelpModalOpen}
                  >
                    <Icon
                      className="flex-none text-brand-secondary"
                      icon="solar:call-chat-rounded-linear"
                      width={20}
                    />
                    {!isSidebarCompact && `${t("Need Help")}?`}
                  </Button>
                </TooltipTrigger>
                <TooltipContent placement="right">{`${t("Need Help")}?`}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip isDisabled={!isSidebarCompact}>
              <TooltipTrigger>
                <Button
                  className={`btn-brand-primary text-sm mx-4 h-10 ${isSidebarCompact ? "justify-center" : "justify-start"}`}
                  data-id="feedback-button"
                  isIconOnly={isSidebarCompact}
                  size="md"
                  onPress={onModalOpen}
                >
                  <Icon
                    className="flex-none text-default-500 text-brand-secondary"
                    icon="solar:question-circle-linear"
                    width={20}
                  />
                  {!isSidebarCompact && t("Feedback")}
                </Button>
              </TooltipTrigger>
              <TooltipContent placement="right">{t("Feedback")}</TooltipContent>
            </Tooltip>

            {isModalOpen && (
              <ModalOverlay isOpen={isModalOpen} size="2xl" onOpenChange={onModalSetOpen}>
                <ModalHeader className="flex flex-col gap-1 font-bold text-brand-primary dark:text-foreground!">
                  {t("Feedback")}
                </ModalHeader>
                <ModalBody>
                  <form onSubmit={handleFeedbackSubmit}>
                    <label className="text-sm font-medium">{t("Your Feedback")}</label>
                    <TextArea
                      aria-label="Feedback input"
                      className="w-full"
                      minRows={3}
                      name="feedback"
                      placeholder={t("Enter your feedback here...")}
                    />
                    <div className="flex justify-end gap-2 mt-4">
                      <Button
                        aria-label="Close feedback modal"
                        className="text-white"
                        style={{
                          backgroundColor: isDark
                            ? brand.colors.dark.tertiary
                            : brand.colors.light.tertiary,
                        }}
                        variant="primary"
                        onPress={() => onModalSetOpen(false)}
                      >
                        {t("Close")}
                      </Button>
                      <Button
                        aria-label="Submit feedback"
                        className="btn-brand-primary text-sm"
                        type="submit"
                      >
                        {t("Submit")}
                      </Button>
                    </div>
                  </form>
                </ModalBody>
              </ModalOverlay>
            )}

            {isHelpModalOpen && (
              <ModalOverlay isOpen={isHelpModalOpen} size="lg" onOpenChange={onHelpModalSetOpen}>
                <ModalHeader className="flex flex-col gap-1 font-bold text-brand-primary dark:text-foreground!">
                  {t("Need Help?")}
                </ModalHeader>
                <ModalBody>
                  <div className="flex flex-col gap-4 text-base text-default-700">
                    <div className="text-default-700">
                      {t(
                        "If you need assistance, our support team is here to help you. You can reach us through the following channel:",
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                      <Button
                        as="a"
                        className="btn-brand-primary text-sm"
                        href={brand.helpUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <Icon height={22} icon="solar:call-chat-rounded-linear" width={22} />{" "}
                        {t("Live Chat with InvestAI Support")}
                      </Button>
                      <div className="text-xs text-default-500 text-center mt-2">
                        {t("We are available and happy to assist you!")}
                      </div>
                    </div>
                  </div>
                </ModalBody>
              </ModalOverlay>
            )}
          </div>
        </div>
      )}

      {/* Chat History Search Modal */}
      <ChatHistorySearchModal
        chatHistory={chatHistory}
        isOpen={isSearchModalOpen}
        onOpenChange={onSearchModalSetOpen}
        onSelectChat={handleSearchSelectChat}
      />

      <div className="w-full flex-1 flex-col overflow-hidden">
        <main className="h-full w-full overflow-auto">{children}</main>
      </div>
    </div>
  );
}
